import styled from 'styled-components'
import { createContext, FC, useContext, useRef, useState } from 'react'

import { Modal } from 'Components/Modal/Modal'
import { StyledButton } from 'Components/Buttons/StyledButton'
import { ModalHeader } from 'Components/Modal/ModalHeader'

import { spacingSM } from 'Utils/styles/spacing'
import { ThinDarkGreenText } from 'Utils/styles/text'
import {
  ConfirmationAnswer,
  ConfirmationModalContent,
  ConfirmationModalContextProviderProps,
  ModalContextType,
  useModalShow
} from 'Hooks/modals/useModalShow'

const ButtonContainer = styled.div`
  display: flex;
  gap: ${spacingSM};
  justify-content: end;
`

const UseConfirmationModal = createContext<ModalContextType<ConfirmationAnswer>>(
  {} as ModalContextType<ConfirmationAnswer>
)

const ConfirmationModalProvider: FC<ConfirmationModalContextProviderProps> = (props) => {
  const { setShow, show, onHide } = useModalShow()

  const [content, setContent] = useState<ConfirmationModalContent | null>()
  const resolver = useRef<(value: ConfirmationAnswer) => void>()

  const showModal = (
    title: string,
    message: string | JSX.Element,
    confirmButtonId = 'im.sure.label',
    cancelButtonId = 'cancel.label'
  ): Promise<ConfirmationAnswer> => {
    setContent({
      title,
      message,
      confirmButtonId,
      cancelButtonId
    })
    setShow(true)
    return new Promise((resolve) => {
      resolver.current = resolve
    })
  }

  const answer = (value: ConfirmationAnswer) => {
    resolver.current?.(value)
    resolver.current = undefined
    onHide()
  }

  return (
    <UseConfirmationModal.Provider
      value={{
        showModal
      }}
    >
      {props.children}

      {content && (
        <Modal
          showModal={show}
          setShowModal={(value) => {
            if (!value) answer(undefined)
          }}
          maxWidth={500}
        >
          <ModalHeader>{content.title}</ModalHeader>

          <ThinDarkGreenText>{content.message}</ThinDarkGreenText>

          <ButtonContainer>
            <StyledButton id={content.cancelButtonId} onClick={() => answer(false)} />
            <StyledButton id={content.confirmButtonId} primary onClick={() => answer(true)} />
          </ButtonContainer>
        </Modal>
      )}
    </UseConfirmationModal.Provider>
  )
}

const useConfirmationModalContext = (): ModalContextType<ConfirmationAnswer> =>
  useContext(UseConfirmationModal)

export { useModalShow, useConfirmationModalContext }

export default ConfirmationModalProvider
